import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import * as Sentry from '@sentry/browser';

/**
 * useCompanyLocationRecordsReassign
 * Provides a callable-based reassign operation for companyLocationRecords.
 *
 * @param {string} fromBreweryId - The donor brewery ID (current parent).
 * @returns {{ reassignRecords: Function, loading: boolean, errorMessage: string, clearError: Function }}
 */
export function useCompanyLocationRecordsReassign(fromBreweryId) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  /**
   * Reassign one or more companyLocationRecords to a new parent brewery.
   *
   * @param {string} toBreweryId - The recipient brewery ID.
   * @param {string[]} companyLocationIds - Array of record document IDs to reassign.
   * @returns {Promise<{ updated: boolean, updatedCount: number, correlationId?: string, error?: string }>}
   */
  const reassignRecords = useCallback(
    async (toBreweryId, companyLocationIds) => {
      if (
        !fromBreweryId ||
        !toBreweryId ||
        fromBreweryId === toBreweryId ||
        !Array.isArray(companyLocationIds)
      ) {
        return { updated: false, updatedCount: 0 };
      }

      const ids = companyLocationIds.filter(
        (id) => typeof id === 'string' && id.trim() !== '',
      );
      if (!ids.length) {
        return { updated: false, updatedCount: 0 };
      }

      if (!isMountedRef.current) {
        return { updated: false, updatedCount: 0 };
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const reassignFn = httpsCallable(
          functions,
          'callableCompanyLocationReassignCompanyLocations-reassignCL',
        );
        const result = await reassignFn({
          fromBreweryId,
          toBreweryId,
          companyLocationIds: ids,
        });
        const updatedCount = Number(result?.data?.updatedCount ?? 0);
        const correlationId = result?.data?.correlationId;
        return {
          updated: updatedCount > 0,
          updatedCount,
          correlationId,
        };
      } catch (err) {
        Sentry.captureException(err);
        if (isMountedRef.current) {
          const friendly =
            'Failed to reassign records. Please try again or contact support.';
          setErrorMessage(friendly);
          return {
            updated: false,
            updatedCount: 0,
            error: friendly,
          };
        }
        return {
          updated: false,
          updatedCount: 0,
        };
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [fromBreweryId],
  );

  return {
    reassignRecords,
    loading,
    errorMessage,
    clearError,
  };
}


